/**
 * 한국어 "내 주변(near me)" 카피 — 주요 9개 서비스 클러스터.
 *
 * 수작업 번역(빌드/런타임 모두 AI 크레딧 소모 없음). 사실관계는
 * ./registry.ts / ./en-copy.ts / ./ja-copy.ts 와 일치시켰습니다.
 *  - 태국은 헤이그 아포스티유 협약 가입국이 아니므로, 해외에서 사용할 태국
 *    공문서는 외교부 영사국의 인증(Legalization)을 받고 필요 시 주태국 해당국
 *    대사관 인증을 추가합니다.
 *  - 가격은 게시하지 않고 건별 견적으로 안내합니다. 처리 기간이나 수리(접수)
 *    결과는 보장하지 않습니다. 수리 여부는 제출 기관이 결정합니다.
 */

import type { NearServiceKey } from "./registry";

export type NearKoCopy = {
  /** H1·타이틀에 쓰는 짧은 서비스명. */
  label: string;
  /** 답변 우선 블록용 사실 기반 정의(1~2문장). */
  definition: string;
  /** 진행 순서. */
  chain: string[];
  /** 준비 서류. */
  checklist: string[];
  /** 자주 발생하는 반송 사유·주의사항. */
  pitfalls: string[];
  /** 원격 처리 범위. */
  remote: string;
};

export const NEAR_KO_KEYS = [
  "notary",
  "naati",
  "sworn",
  "pcc",
  "translation",
  "moj",
  "visa",
  "lgz",
  "apostille",
] as const satisfies readonly NearServiceKey[];

export type NearKoKey = (typeof NEAR_KO_KEYS)[number];

const LEGALIZE_CHAIN =
  "제출 기관이 요구하는 경우 외교부 영사국(Legalization Division) 인증을 받고, 이어서 주태국 해당국 대사관 인증을 추가합니다.";

export const NEAR_KO_COPY: Record<NearKoKey, NearKoCopy> = {
  notary: {
    label: "태국 공증업무 변호사(Notarial Services Attorney)",
    definition:
      "태국에는 독립된 공증인 제도가 없습니다. 서명 인증, 사본 인증, 진술서(Affidavit), 회사 수권 서류 인증은 태국 변호사협회(Lawyers Council of Thailand)가 인가한 공증업무 변호사가 수행하며, 주로 해외 제출용으로 사용됩니다.",
    chain: [
      "서류 전체를 컬러 스캔으로 보내고, 제출 기관 또는 사용할 국가를 알려 주십니다.",
      "실제로 인증이 필요한 페이지와 올바른 순서를 저희가 확인합니다.",
      "서명인이 공증업무 변호사 앞에서 서명합니다(직접 출석 또는 제출 기관이 허용하는 방식).",
      "인증 문구 기재, 직인, 등록부 기록을 진행합니다.",
      LEGALIZE_CHAIN,
      "인증된 원본을 택배로 발송하거나 사무실에서 수령합니다.",
    ],
    checklist: [
      "서명인 전원의 여권 또는 태국 신분증(유효기간 내, 서류와 표기 일치).",
      "인증 대상 서류의 최종본(미서명 상태).",
      "법인 명의인 경우 법인 등기부(DBD) 등본과 수권 이사 명단.",
      "타인을 대리해 서명하는 경우 위임장(Power of Attorney).",
      "제출 기관에서 서면 요건을 받았다면 그 사본.",
    ],
    pitfalls: [
      "인증 일정 전에 미리 서명해 원본을 다시 만들어야 하는 경우.",
      "이름 표기가 여권과 불일치 — 해외 반송 사유 1위입니다.",
      "태국 서류에 아포스티유를 받을 수 있다고 오해하는 경우. 통상 외교부 인증 + 대사관 인증 경로입니다.",
      "제출 기관이 인증 원본을 요구하는데 인증 사본만 제출하는 경우.",
      "법인 등기부가 오래된 경우. 다수 기관은 최근 발급본을 요구합니다.",
    ],
    remote:
      "서류 검토, 문구 확인, 이후 인증 절차는 원격으로 진행할 수 있습니다. 대면 서명 단계만 출석이 필요합니다.",
  },
  naati: {
    label: "NAATI 인증 번역",
    definition:
      "NAATI 인증 번역은 호주 NAATI 자격을 보유한 번역사가 작성하고 번역사의 인증 문구와 스탬프를 붙인 번역입니다. 호주 정부기관, 대학, 비자 심사센터에서 일반적으로 요구합니다.",
    chain: [
      "컬러 스캔을 보내 주시면 선명도, 직인, 페이지 수를 점검합니다.",
      "제출처(내무부, 대학, 자격인정기관 등)와 여권상 이름 표기를 확인합니다.",
      "NAATI 인증 번역사가 번역하고 인증 문구와 스탬프를 부여합니다.",
      "원본과 항목별로 대조합니다(이름·날짜·숫자·직인 문자).",
      "전자 인증본을 납품하고 필요 시 종이 원본을 발송합니다.",
    ],
    checklist: [
      "여권 인적사항 페이지(로마자 표기 확인용).",
      "각 서류의 전체 페이지 컬러 스캔(뒷면·직인 포함).",
      "과거 제출한 기존 번역본(있는 경우) — 용어 통일을 위해.",
      "제출 기관이 제시한 번역 관련 구체적 요건(받은 경우).",
    ],
    pitfalls: [
      "스캔이 잘려 공인이 보이지 않아 번역 일체가 무효가 되는 경우.",
      "같은 이름이 서류마다 다른 표기로 기재된 경우.",
      "인증 원본을 먼저 받아야 하는데 번역을 먼저 진행한 경우.",
      "흑백 사본으로 직인 색상을 확인할 수 없는 경우.",
    ],
    remote: "전 과정을 온라인으로 완료할 수 있어 방문이 필요하지 않습니다.",
  },
  sworn: {
    label: "선서 번역사·법원 등록 번역사 번역",
    definition:
      "일부 국가는 선서 번역사 또는 공적 명부에 등록된 번역사의 번역만 수리합니다. 태국에서는 해당 기관 또는 법원 명부에 등재된 번역사가 작성하고, 인증 페이지에 번역사 신분과 원본 정보를 기재합니다.",
    chain: [
      "제출 국가가 인정하는 명부(법원·대사관·부처 명부)를 확인합니다.",
      "스캔 선명도와 누락 페이지 여부를 점검합니다.",
      "등록 번역사가 번역하고 인증 페이지를 작성합니다.",
      LEGALIZE_CHAIN,
      "직인이 찍힌 종이 서류를 발송합니다.",
    ],
    checklist: [
      "이름 표기에 사용할 여권 또는 신분증.",
      "첨부 서류와 모든 직인이 포함된 완전한 스캔.",
      "제출 기관명과 필요한 언어 조합.",
      "제출 기관이 정한 기한(있는 경우).",
    ],
    pitfalls: [
      "명부 종류를 잘못 선택하면 번역이 정확해도 반송됩니다.",
      "같은 건에서 인명·지명 번역이 통일되지 않은 경우.",
      "첨부 페이지가 빠져 제출 기관이 서류 미비로 판단하는 경우.",
      "먼저 제책·간인을 해버려 이후 인증 절차가 불가능해지는 경우.",
    ],
    remote: "번역과 인증 절차는 원격으로 진행하고, 종이 원본은 택배로 납품합니다.",
  },
  pcc: {
    label: "태국 범죄경력증명서(Police Clearance Certificate)",
    definition:
      "태국 범죄경력증명서는 왕립 태국경찰 범죄기록과에서 발급합니다. 비자, 이민, 취업, 자격 신청에 사용되며 통상 인증 번역과 이후 인증 절차가 함께 필요합니다.",
    chain: [
      "제출 기관이 요구하는 증명서 종류와 사용 목적 문구를 확인합니다.",
      "신청 서류를 준비하고 지문 채취 요건을 확인합니다.",
      "범죄기록과에 접수하고 진행 상황을 추적합니다.",
      "발급 후 인증 번역을 진행합니다.",
      LEGALIZE_CHAIN,
    ],
    checklist: [
      "여권(인적사항 페이지) 또는 태국 신분증.",
      "제출 기관이 정한 양식의 지문 카드(해당 시).",
      "사용 목적과 제출 국가를 밝힌 서면.",
      "대리 신청 시 위임장과 대리인 신분증.",
    ],
    pitfalls: [
      "제출 기관이 요구하는 문구가 없는 종류로 발급받는 경우.",
      "지문 채취 양식이 제출 국가 규격과 맞지 않는 경우.",
      "유효기간(다수 기관은 3~6개월)이 지나 다시 발급해야 하는 경우.",
      "번역과 인증에 필요한 기간을 일정에 넣지 않는 경우.",
    ],
    remote:
      "서류 준비, 번역, 이후 인증은 원격으로 지원합니다. 일부 단계는 본인 또는 수임인의 출석이 필요합니다.",
  },
  translation: {
    label: "공문서 인증 번역",
    definition:
      "인증 번역은 번역사 또는 번역기관이 원본과의 일치를 확인하는 서명·직인이 포함된 번역입니다. 태국 관공서, 대사관, 해외 기관 제출용으로 널리 요구되며 이후 인증 절차와 함께 진행되는 경우가 많습니다.",
    chain: [
      "제출 기관과 필요한 언어 조합, 요구되는 인증 형태를 확인합니다.",
      "전체 페이지 컬러 스캔을 점검합니다(직인·뒷면 포함).",
      "번역 후 이름·날짜·숫자를 원본과 대조합니다.",
      "인증 문구와 직인을 부여합니다.",
      LEGALIZE_CHAIN,
    ],
    checklist: [
      "로마자 이름 표기 확인용 여권.",
      "번역 대상 서류의 전체 페이지 스캔.",
      "기존 번역본(있는 경우) — 표기 통일용.",
      "제출 기관이 공표한 번역 요건.",
    ],
    pitfalls: [
      "요약 번역만 제출해 전체 번역을 다시 요구받는 경우.",
      "서류마다 이름 표기가 달라 동일인으로 인정받지 못하는 경우.",
      "원본 재발급이 필요한 상황에서 번역을 먼저 진행하는 경우.",
      "제출 기관이 특정 언어를 요구하는데 영어 번역만 준비하는 경우.",
    ],
    remote: "전 과정을 원격으로 진행할 수 있으며, 종이 원본이 필요하면 발송합니다.",
  },
  moj: {
    label: "법무부·관공서 관련 서류 절차",
    definition:
      "태국 법무부 및 관련 기관이 다루는 서류(기록 조회, 증명서, 기관 발행 공문서)는 기관이 정한 양식·수권·제출 경로에 따라 진행하며, 이후 번역과 인증이 필요한 경우가 많습니다.",
    chain: [
      "필요한 서류를 어느 기관·부서가 발급하는지 특정합니다.",
      "신청서, 신분증, 위임장을 준비합니다.",
      "정해진 경로로 접수하고 진행 상황을 추적합니다.",
      "수령 후 인증 번역사가 번역합니다.",
      LEGALIZE_CHAIN,
    ],
    checklist: [
      "신청인의 여권 또는 태국 신분증.",
      "대리 신청에 필요한 위임장과 대리인 신분증.",
      "신청 사유와 관련된 기존 서류 또는 사건번호.",
      "제출 기관의 서면 요건(있는 경우).",
    ],
    pitfalls: [
      "잘못된 기관·부서에 접수해 한 차례 기간을 잃는 경우.",
      "위임장 문구가 이번 신청 사항을 포함하지 않는 경우.",
      "발급 후 번역·인증을 미뤄 유효기간이 임박하는 경우.",
      "모든 공문서가 당일 발급된다고 가정하는 경우(대부분 당일이 아닙니다).",
    ],
    remote:
      "서류 준비, 번역, 인증은 원격으로 지원합니다. 일부 기관은 본인 또는 수임인의 출석을 요구합니다.",
  },
  visa: {
    label: "비자 서류 준비 및 컨설팅",
    definition:
      "비자 서류 준비란 보유하신 증빙 서류를 목적 국가가 정한 형식으로 정리하는 작업입니다. 인증 번역, 자금·관계 증명, 필요 시 공증과 인증이 포함됩니다. 심사 권한은 항상 대사관 또는 이민 당국에 있습니다.",
    chain: [
      "비자 종류와 해당 대사관이 현재 공표한 목록을 확인합니다.",
      "보유 서류를 점검해 부족분과 재발급 대상을 정리합니다.",
      "인증 번역을 작성하고 필요 시 공증을 진행합니다.",
      LEGALIZE_CHAIN,
      "심사관이 읽기 쉬운 순서로 파일을 구성합니다.",
    ],
    checklist: [
      "여권(과거 비자·출입국 스탬프 포함).",
      "신분관계 서류(출생·혼인·주민등록 등 해당 항목).",
      "자금 증명(신청 구분에 맞는 양식).",
      "관계 증명(가족·배우자 비자의 경우).",
      "대사관이 공표한 최신 체크리스트.",
    ],
    pitfalls: [
      "오래된 체크리스트를 사용해 양식 변경을 놓치는 경우.",
      "수리 기간이 지난 오래된 증명서를 제출하는 경우.",
      "번역의 이름 표기가 여권과 일치하지 않는 경우.",
      "비자 승인을 보장한다는 업체를 신뢰하는 경우. 승인은 당국만 결정합니다.",
    ],
    remote:
      "서류 점검, 번역, 구성 설계는 모두 원격으로 지원합니다. 면접과 생체정보 채취는 본인이 출석합니다.",
  },
  lgz: {
    label: "외교부 영사국 인증(Legalization)",
    definition:
      "해외에서 사용할 태국 공문서는 외교부 영사국(Legalization Division)에서 서명·직인의 진정성 인증을 받습니다. 태국은 아포스티유 협약 가입국이 아니므로 다수의 경우 주태국 해당국 대사관 인증이 추가로 필요합니다.",
    chain: [
      "서류 종류별로 인증 가능한 형태(원본·인증 사본·번역)를 확인합니다.",
      "필요 시 발급 기관에서 최신 원본을 발급받습니다.",
      "인증 번역을 작성합니다(대부분 태국어⇄영어).",
      "외교부 영사국에 접수하고 인증을 받습니다.",
      "제출 기관이 요구하는 경우 주태국 해당국 대사관 인증을 추가합니다.",
    ],
    checklist: [
      "인증 대상 원본 또는 발급 기관의 인증 사본.",
      "신청인 또는 수임인의 여권·신분증.",
      "대리 접수 시 위임장.",
      "인증 번역(대상 서류에 필요한 경우).",
      "사용할 국가와 제출 기관명.",
    ],
    pitfalls: [
      "아포스티유를 받으려 하는 경우 — 태국에서는 발급되지 않습니다.",
      "인증 대상으로 인정되지 않는 형태의 사본을 제출하는 경우.",
      "번역 양식이 요건과 맞지 않아 인증 단계에서 반송되는 경우.",
      "대사관 인증이 별도 절차임을 놓쳐 일정이 부족해지는 경우.",
    ],
    remote:
      "서류 준비, 번역, 접수 대행은 원격으로 지원합니다. 위임장 서명에는 본인의 관여가 필요합니다.",
  },
  apostille: {
    label: "아포스티유·인증(태국 서류의 경우)",
    definition:
      "태국은 헤이그 아포스티유 협약 가입국이 아니므로 태국 공문서에는 아포스티유가 부여되지 않습니다. 동등한 효력을 얻는 경로는 외교부 영사국 인증과 필요 시 주태국 해당국 대사관 인증입니다. 해외 발행 서류는 발행국에서 아포스티유를 받을 수 있습니다.",
    chain: [
      "서류가 태국 발행인지 해외 발행인지 구분합니다(경로가 완전히 다릅니다).",
      "태국 발행: 최신 원본을 확보하고 인증 번역을 작성합니다.",
      "외교부 영사국 인증을 받습니다.",
      "제출 기관이 요구하는 경우 주태국 해당국 대사관 인증을 추가합니다.",
      "해외 발행: 발행국에서 아포스티유를 받고 태국어 인증 번역을 작성합니다.",
    ],
    checklist: [
      "원본(또는 발급 기관의 인증 사본).",
      "여권 또는 신분증.",
      "대리 진행 시 위임장.",
      "사용할 국가·제출 기관과 사용 목적.",
      "해외 서류의 경우 아포스티유가 부착된 원본.",
    ],
    pitfalls: [
      "'태국 아포스티유'를 요청하는 경우 — 존재하지 않습니다.",
      "번역을 먼저 마쳤는데 원본 재발급이 필요해 무용해지는 경우.",
      "주태국 대사관 인증 단계를 일정에 넣지 않는 경우.",
      "제출 기관이 영어 외 언어를 요구하는데 영문 번역만 준비하는 경우.",
    ],
    remote:
      "경로 확인, 번역, 접수는 원격으로 지원합니다. 위임장 서명만 본인의 관여가 필요합니다.",
  },
};

/** 주요 주(짱왓)·지역의 한국어 표기(미수록 시 영문명 사용). */
export const NEAR_KO_AREA: Record<string, string> = {
  nonthaburi: "논타부리",
  pathumthani: "빠툼타니",
  samutprakan: "사뭇프라깐",
  chonburi: "촌부리",
  pattaya: "파타야",
  rayong: "라용",
  chiangmai: "치앙마이",
  chiangrai: "치앙라이",
  khonkaen: "콘깬",
  udonthani: "우돈타니",
  nakhonratchasima: "나콘랏차시마(코랏)",
  phuket: "푸껫",
  suratthani: "수랏타니",
  songkhla: "송클라",
  ubonratchathani: "우본랏차타니",
};
